import React, { useState, useEffect, useRef, DragEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  InputAdornment,
  Paper,
  Chip,
  ButtonBase,
} from '@mui/material';
import {
  X,
  Briefcase,
  FileText,
  UploadCloud,
  Search,
  Sparkles,
  CheckCircle2,
  FileUp,
} from 'lucide-react';
import { toast } from '../../../ui/toast';
import { getJobOpeningList } from '../../../utils/api_service/JobOpenings';

export interface ExtractedJdData {
  jobTitle: string;
  experience: string;
  technicalSkills: string[];
  behavioralAttributes: string[];
  educations: string[];
  certifications: string[];
  industryExperience: string[];
  searchString: string;
  fileName?: string;
}

interface UseJdModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateFromFile: (file: File) => Promise<void>;
  onCreateFromText: (text: string) => Promise<void>;
  onCreateFromOpening: (jobOpeningId: string) => Promise<void>;
  isCreating?: boolean;
  onAutoFillSearch?: (searchString: string) => void;
  onAutoFillAnalytics?: (extracted: ExtractedJdData) => void;
  onClearSearch?: () => void;
  onClearAnalytics?: () => void;
}

type TabType = 'upload' | 'paste' | 'opening';

export const UseJdModal: React.FC<UseJdModalProps> = ({
  isOpen,
  onClose,
  onCreateFromFile,
  onCreateFromText,
  onCreateFromOpening,
  isCreating = false,
  onAutoFillSearch,
  onAutoFillAnalytics,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('opening');

  // Tab 1: Upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tab 2: Paste
  const [jdText, setJdText] = useState('');

  // Tab 3: Opening
  const [openings, setOpenings] = useState<any[]>([]);
  const [openingsLoading, setOpeningsLoading] = useState(false);
  const [openingSearchQuery, setOpeningSearchQuery] = useState('');
  const [selectedOpeningId, setSelectedOpeningId] = useState<string | null>(null);

  // Extracted data for auto-fill preview
  const [extractedData, setExtractedData] = useState<ExtractedJdData | null>(null);

  useEffect(() => {
    if (isOpen && activeTab === 'opening' && openings.length === 0) {
      fetchOpenings();
    }
  }, [isOpen, activeTab]);

  const fetchOpenings = async () => {
    setOpeningsLoading(true);
    try {
      const res = await getJobOpeningList({} as any, 1, 50);
      if (res && res.json) {
        const data = await res.json();
        const items = data.data || data.openings || data.jobOpenings || data;
        setOpenings(Array.isArray(items) ? items : []);
      }
    } catch (error) {
      console.error('Failed to fetch job openings', error);
    } finally {
      setOpeningsLoading(false);
    }
  };

  const extractJdInformation = (text: string, fileName: string): ExtractedJdData => {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);

    let jobTitle = 'Software Engineer';
    const titleMatch = text.match(/(?:job title|position|role)\s*:\s*([^\n\r]+)/i);
    if (titleMatch && titleMatch[1]) {
      jobTitle = titleMatch[1].trim();
    } else if (lines.length > 0) {
      const firstLine = lines[0].replace(/^(job description|jd|position)\s*[-:]?\s*/i, '');
      if (firstLine.length < 60) {
        jobTitle = firstLine;
      }
    }

    let experience = '3-5 years';
    const expMatch = text.match(/(\d+\s*[-–to]*\s*\d*|\d+\+?)\s*(?:years?|yrs?)(?:\s+of)?\s+experience/i);
    if (expMatch) {
      experience = expMatch[0];
    }

    const commonSkills = [
      'React', 'React.js', 'Node.js', 'TypeScript', 'JavaScript', 'Python', 'Java', 'C++',
      'AWS', 'Docker', 'Kubernetes', 'SQL', 'PostgreSQL', 'MongoDB', 'GraphQL', 'REST API',
      'Git', 'CI/CD', 'Agile', 'HTML', 'CSS', 'Tailwind', 'Next.js', 'Redux', 'UiPath',
      'RPA', 'Computer Vision', 'Process Mining', 'Data Science', 'Machine Learning',
      'PHP', 'C#', '.NET', 'Angular', 'Vue.js', 'GCP', 'Azure', 'Microservices'
    ];
    const technicalSkills: string[] = [];
    commonSkills.forEach((skill) => {
      const regex = new RegExp(`\\b${skill.replace('.', '\\.')}\\b`, 'i');
      if (regex.test(text) && !technicalSkills.includes(skill)) {
        technicalSkills.push(skill);
      }
    });

    if (technicalSkills.length === 0) {
      technicalSkills.push('JavaScript', 'TypeScript', 'React', 'Node.js');
    }

    const commonBehaviors = [
      'Communication', 'Leadership', 'Problem Solving', 'Teamwork',
      'Analytical Thinking', 'Time Management', 'Adaptability', 'Creativity',
      'Attention to Detail', 'Stakeholder Management'
    ];
    const behavioralAttributes: string[] = [];
    commonBehaviors.forEach((attr) => {
      const regex = new RegExp(`\\b${attr}\\b`, 'i');
      if (regex.test(text) && !behavioralAttributes.includes(attr)) {
        behavioralAttributes.push(attr);
      }
    });
    if (behavioralAttributes.length === 0) {
      behavioralAttributes.push('Communication', 'Problem Solving', 'Teamwork');
    }

    const searchString = jobTitle;

    return {
      jobTitle,
      experience,
      technicalSkills,
      behavioralAttributes,
      educations: ["Bachelor's Degree in Computer Science"],
      certifications: [],
      industryExperience: [],
      searchString,
      fileName,
    };
  };

  const handleSearchCandidates = async () => {
    try {
      if (activeTab === 'upload' && selectedFile) {
        await onCreateFromFile(selectedFile);
        onClose();
      } else if (activeTab === 'paste' && jdText.trim()) {
        await onCreateFromText(jdText.trim());
        onClose();
      } else if (activeTab === 'opening' && selectedOpeningId) {
        await onCreateFromOpening(selectedOpeningId);
        onClose();
      }
    } catch (err) {
      console.error('Failed to create search', err);
    }
  };

  const handleFileProcess = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit', { position: 'bottom-center' });
      return;
    }

    setSelectedFile(file);

    if (file.type.includes('text') || file.name.endsWith('.txt')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = (e.target?.result as string) || '';
        setExtractedData(extractJdInformation(text, file.name));
      };
      reader.readAsText(file);
    } else {
      const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      setExtractedData({
        jobTitle: cleanName,
        experience: 'Analyzed on search',
        technicalSkills: ['AI-extracted skills'],
        behavioralAttributes: ['AI-extracted traits'],
        educations: [],
        certifications: [],
        industryExperience: [],
        searchString: cleanName,
        fileName: file.name,
      });
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileProcess(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileProcess(e.target.files[0]);
    }
  };

  const handleTextChange = (text: string) => {
    setJdText(text);
    if (text.trim().length > 20) {
      setExtractedData(extractJdInformation(text, 'Pasted JD'));
    }
  };

  const handleAutoFillSearchClick = () => {
    const data = extractedData || (jdText.trim() ? extractJdInformation(jdText, 'Pasted JD') : null);
    if (!data) {
      toast.warning('Please provide a JD file or text first.', { position: 'bottom-center' });
      return;
    }
    if (onAutoFillSearch) {
      onAutoFillSearch(data.searchString);
      toast.success(`Talent Pool search set to "${data.searchString}"!`, { position: 'bottom-center' });
      onClose();
    }
  };

  const handleAutoFillAnalyticsClick = () => {
    const data = extractedData || (jdText.trim() ? extractJdInformation(jdText, 'Pasted JD') : null);
    if (!data) {
      toast.warning('Please provide a JD file or text first.', { position: 'bottom-center' });
      return;
    }
    if (onAutoFillAnalytics) {
      onAutoFillAnalytics(data);
      toast.success('Analytic Config populated with extracted criteria!', { position: 'bottom-center' });
      onClose();
    }
  };

  if (!isOpen) return null;

  const isSearchDisabled =
    isCreating ||
    (activeTab === 'upload' && !selectedFile) ||
    (activeTab === 'paste' && !jdText.trim()) ||
    (activeTab === 'opening' && !selectedOpeningId);

  const filteredOpenings = openings.filter((op: any) =>
    (op.title || op.name || op.jobTitle || '').toLowerCase().includes(openingSearchQuery.toLowerCase())
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: 'grey.900',
          color: 'white',
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
            Create New Search
          </Typography>
          <Typography variant="caption" sx={{ color: 'grey.400' }}>
            Extract requirements and search your candidate pool
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'grey.400', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
        >
          <X size={18} />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{ minHeight: 44 }}
        >
          <Tab
            value="opening"
            label="From Opening"
            icon={<Briefcase size={15} />}
            iconPosition="start"
            sx={{ minHeight: 44, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none' }}
          />
          <Tab
            value="paste"
            label="Paste JD"
            icon={<FileText size={15} />}
            iconPosition="start"
            sx={{ minHeight: 44, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none' }}
          />
          <Tab
            value="upload"
            label="Upload JD"
            icon={<UploadCloud size={15} />}
            iconPosition="start"
            sx={{ minHeight: 44, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      {/* Modal Body */}
      <DialogContent sx={{ p: 3, minHeight: 320 }}>
        <Stack spacing={2.5}>
          {activeTab === 'upload' && (
            <Stack spacing={2}>
              <Box
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(e.type === 'dragover');
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragOver ? 'primary.main' : 'grey.300',
                  bgcolor: isDragOver ? 'primary.50' : 'grey.50',
                  borderRadius: 3,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.light',
                    bgcolor: 'grey.100',
                  },
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
                <Stack alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 1,
                      color: 'primary.main',
                    }}
                  >
                    <FileUp size={22} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Click or drag your JD file here
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Supports PDF, DOCX, and TXT (Max 10MB)
                  </Typography>
                </Stack>
              </Box>

              {selectedFile && (
                <Paper
                  elevation={0}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'success.50',
                    border: '1px solid',
                    borderColor: 'success.light',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Chip
                      label={selectedFile.name.split('.').pop() || 'FILE'}
                      size="small"
                      color="success"
                      sx={{ height: 20, fontSize: '0.625rem', fontWeight: 700 }}
                    />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.dark', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'success.main', fontSize: '0.625rem', flexShrink: 0 }}>
                      ({(selectedFile.size / 1024).toFixed(0)} KB)
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setExtractedData(null);
                    }}
                    sx={{ color: 'success.dark' }}
                  >
                    <X size={14} />
                  </IconButton>
                </Paper>
              )}
            </Stack>
          )}

          {activeTab === 'paste' && (
            <Stack spacing={1}>
              <TextField
                multiline
                rows={8}
                fullWidth
                placeholder="Paste the complete job description, required skills, and qualifications here..."
                value={jdText}
                onChange={(e) => handleTextChange(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem' } }}
              />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                  Word count: {jdText.trim() ? jdText.trim().split(/\s+/).length : 0} words
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                  {jdText.length} characters
                </Typography>
              </Stack>
            </Stack>
          )}

          {activeTab === 'opening' && (
            <Stack spacing={2}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search openings (including closed)..."
                value={openingSearchQuery}
                onChange={(e) => setOpeningSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
              />

              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  maxHeight: 220,
                  overflowY: 'auto',
                }}
              >
                {openingsLoading ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: 140 }}>
                    <CircularProgress size={24} />
                  </Stack>
                ) : filteredOpenings.length === 0 ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: 140 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      No openings found.
                    </Typography>
                  </Stack>
                ) : (
                  <Stack>
                    {filteredOpenings.map((op: any) => {
                      const id = op._id || op.id;
                      const isSelected = selectedOpeningId === id;
                      return (
                        <ButtonBase
                          key={id}
                          onClick={() => {
                            setSelectedOpeningId(id);
                            const title = op.title || op.name || op.jobTitle || 'Job Opening';
                            setExtractedData({
                              jobTitle: title,
                              experience: 'From Opening',
                              technicalSkills: op.skills || ['Full Stack'],
                              behavioralAttributes: [],
                              educations: [],
                              certifications: [],
                              industryExperience: [],
                              searchString: title,
                              fileName: title,
                            });
                          }}
                          sx={{
                            p: 1.5,
                            textAlign: 'left',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid',
                            borderColor: 'grey.100',
                            bgcolor: isSelected ? 'primary.50' : 'transparent',
                            borderLeft: isSelected ? '4px solid' : 'none',
                            borderLeftColor: 'primary.main',
                            '&:hover': {
                              bgcolor: isSelected ? 'primary.50' : 'grey.100',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: isSelected ? 'primary.dark' : 'text.primary',
                              }}
                            >
                              {op.title || op.name || op.jobTitle || 'Untitled Opening'}
                            </Typography>
                            {op.jobReferenceNumber && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.625rem' }}>
                                #{op.jobReferenceNumber}
                              </Typography>
                            )}
                          </Box>
                          {op.status && (
                            <Chip
                              label={op.status}
                              size="small"
                              sx={{ height: 20, fontSize: '0.625rem', fontWeight: 600 }}
                            />
                          )}
                        </ButtonBase>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            </Stack>
          )}

          {/* Extracted Preview Badge */}
          {extractedData && (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.100',
                bgcolor: 'primary.50',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircle2 size={16} color="#4f46e5" />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                    Target Title: {extractedData.jobTitle}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem' }}>
                  {extractedData.experience}
                </Typography>
              </Stack>
              {extractedData.technicalSkills.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                  {extractedData.technicalSkills.slice(0, 6).map((sk, idx) => (
                    <Chip
                      key={idx}
                      label={sk}
                      size="small"
                      sx={{
                        fontSize: '0.625rem',
                        height: 20,
                        bgcolor: 'background.paper',
                        color: 'primary.dark',
                        border: '1px solid',
                        borderColor: 'primary.200',
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Paper>
          )}

          {/* Quick Auto-Fill Actions */}
          <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              Also Auto-Fill:
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<Sparkles size={13} color="#4f46e5" />}
                onClick={handleAutoFillSearchClick}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  borderColor: 'grey.300',
                  bgcolor: 'background.paper',
                }}
              >
                Talent Pool
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<Sparkles size={13} color="#059669" />}
                onClick={handleAutoFillAnalyticsClick}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  borderColor: 'grey.300',
                  bgcolor: 'background.paper',
                }}
              >
                Analytics Config
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'grey.100', bgcolor: 'grey.50' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 'auto', fontSize: '0.6875rem' }}>
          Search will be saved under <strong>Advanced Search</strong>
        </Typography>
        <Button
          onClick={onClose}
          disabled={isCreating}
          color="inherit"
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSearchCandidates}
          disabled={isSearchDisabled}
          startIcon={isCreating ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8125rem' }}
        >
          {isCreating ? 'Processing...' : 'Create Search'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
